/**
 * Match Score Normalization Utilities
 *
 * Embedding similarity scores from pgvector cosine distance are normalized
 * to a user-friendly 0-100% scale based on observed production data.
 *
 * Production statistics (as of 2026-02):
 * - Range: 0.524 - 0.787 (52.4% - 78.7%)
 * - Median: 0.662 (66.2%)
 * - 5th percentile: 0.328
 * - 95th percentile: 0.785
 *
 * These utilities normalize scores to better reflect match quality,
 * with 0.85 representing an "excellent match" based on semantic similarity research.
 */

/** Minimum expected raw score (below this = 0%) - based on 5th percentile */
const MIN_SCORE = 0.33;

/** Maximum expected raw score (above this = 100%) - excellent match threshold */
const MAX_SCORE = 0.85;

/**
 * Normalize a raw similarity score to a user-friendly 0-100 percentage.
 *
 * Stretches the observed score range (0.33-0.85) to full 0-100%:
 * - 0.33 raw → 0%
 * - 0.59 raw → 50%
 * - 0.85 raw → 100%
 *
 * Examples from production:
 * - 0.524 (min observed) → 37%
 * - 0.662 (median) → 64%
 * - 0.787 (max observed) → 88%
 *
 * @param score - Raw similarity score (0-1) or percentage (0-100)
 * @returns Normalized percentage (0-100), rounded to nearest integer
 */
export function normalizeMatchScore(score: number | null | undefined): number {
  if (!score && score !== 0) return 0;

  // Normalize to decimal (0-1) if percentage was passed
  const rawScore = score > 1 ? score / 100 : score;

  // Apply normalization formula
  const normalized = ((rawScore - MIN_SCORE) / (MAX_SCORE - MIN_SCORE)) * 100;

  // Clamp to 0-100 range
  return Math.round(Math.min(100, Math.max(0, normalized)));
}
